/**
 * サーバ生成サムネイル（両配信モード）。
 *
 * サムネイルはサーバの thumbs 工程が両モードで生成する。無いブック（M4 以前に
 * 取り込んだもの）では null を返し、呼び出し側は従来どおりページ描画へフォールバックする。
 */
import { ViewerError } from '../core/errors';
import type { ManifestSession } from '../core/manifest';
import { FULL_PAGE } from '../core/source';
import type { PageRaster } from '../core/source';
import { signedFetch } from './signed-fetch';
import { thumbUrl } from './image-tiles/urls';

export interface ThumbSource {
  get(pageNo: number, width: number, signal: AbortSignal): Promise<PageRaster>;
}

/** マニフェストがサーバ生成サムネイルを持つときだけ作る。 */
export function createThumbSource(session: ManifestSession): ThumbSource | null {
  const manifest = session.current;
  if (manifest.thumbsAvailable !== true || !manifest.urls.pages) return null;

  return {
    async get(pageNo: number, width: number, signal: AbortSignal): Promise<PageRaster> {
      const response = await signedFetch(
        session,
        (current) => {
          const urls = current.urls.pages;
          if (!urls) throw new ViewerError('E_ACCESS_DENIED', 'ページ配信 URL が許可されていない');
          return thumbUrl(urls, pageNo);
        },
        { signal },
      );
      if (!response.ok) {
        throw new ViewerError('E_HTTP', `サムネイルを取得できない（HTTP ${response.status}）`);
      }
      const bitmap = await createImageBitmap(await response.blob());
      signal.throwIfAborted();
      void width; // サーバ生成の解像度をそのまま使う（UI 側が CSS で収める）

      return {
        pageNo,
        bitmap,
        width: bitmap.width,
        height: bitmap.height,
        region: FULL_PAGE,
        close: () => bitmap.close(),
      };
    },
  };
}
